import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, Zap, TrendingUp, PlusCircle } from 'lucide-react';

export const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const todayCount = user?.listings_found_today || 0;

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
      </div>
    );
  }

  const isTrial = user?.trial_expires_at && !user?.active_subscription;
  const subName = user?.active_subscription?.plan?.name || (isTrial ? 'Пробный период' : 'Нет подписки');
  const daysLeft = isTrial
    ? Math.ceil((new Date(user.trial_expires_at!).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : user?.active_subscription
    ? Math.ceil((new Date(user.active_subscription.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.greeting}>
          Привет, {user?.first_name || 'друг'} 👋
        </h1>
        <p style={styles.subtitle}>Авигатор следит за Avito</p>
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <Zap size={18} style={{ color: '#f59e0b' }} />
          <span style={styles.cardTitle}>Подписка</span>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.planName}>{subName}</div>
          {daysLeft > 0 && (
            <div style={styles.daysLeft}>Осталось: {daysLeft} дней</div>
          )}
          {((isTrial && daysLeft > 0) || user?.active_subscription?.plan?.name === 'FREE_TRIAL') && (
            <div style={{ fontSize: 12, lineHeight: 1.4, marginTop: 6, color: 'var(--tg-theme-hint-color, #8a8a8a)' }}>
              Ознакомительный режим: на платных тарифах проверки чаще, и новые объявления приходят быстрее.
            </div>
          )}
          <div style={styles.searchesInfo}>
            Активных поисков: <b>{user?.active_searches_count || 0}</b> / {user?.active_subscription?.plan?.max_searches || 1}
          </div>
        </div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <TrendingUp size={20} style={{ color: '#10b981' }} />
          <div style={styles.statValue}>{todayCount}</div>
          <div style={styles.statLabel}>Сегодня найдено</div>
        </div>
        <div style={styles.statCard}>
          <Search size={20} style={{ color: '#3b82f6' }} />
          <div style={styles.statValue}>{user?.searches_count || 0}</div>
          <div style={styles.statLabel}>Всего поисков</div>
        </div>
      </div>

      <button
        style={styles.addButton}
        onClick={() => navigate('/add')}
      >
        <PlusCircle size={20} />
        <span>Добавить поиск</span>
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  header: {
    marginBottom: '20px',
  },
  greeting: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
    color: 'var(--tg-theme-text-color, #000)',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #666)',
    margin: '4px 0 0 0',
  },
  card: {
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--tg-theme-hint-color, #666)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  planName: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  daysLeft: {
    fontSize: '14px',
    color: '#f59e0b',
    fontWeight: 500,
  },
  searchesInfo: {
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #666)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '16px',
  },
  statCard: {
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: 'var(--tg-theme-text-color, #000)',
  },
  statLabel: {
    fontSize: '12px',
    color: 'var(--tg-theme-hint-color, #666)',
    textAlign: 'center',
  },
  addButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--tg-theme-button-color, #2481cc)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
};
